import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getModelo, saveModelo } from '../services/api';

const PlusCircle = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>
);
const Trash2 = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
);
const ArrowLeft = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
);

const ModeloForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'novo';

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [secoes, setSecoes] = useState([{ titulo: 'Seção 1', perguntas: [] }]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setIsLoading(true);
      getModelo(id)
        .then(data => {
          setTitulo(data.titulo);
          setDescricao(data.descricao || '');
          setSecoes(data.checklist_secoes_modelo || [{ titulo: 'Seção 1', perguntas: [] }]);
        })
        .catch(err => setError(err.message))
        .finally(() => setIsLoading(false));
    }
  }, [id, isEditing]);

  const handleAddSecao = () => {
    setSecoes([...secoes, { titulo: `Seção ${secoes.length + 1}`, perguntas: [] }]);
  };

  const handleRemoveSecao = (index) => {
    if (secoes.length <= 1) return;
    const novasSecoes = secoes.filter((_, i) => i !== index);
    setSecoes(novasSecoes);
  };

  const handleSecaoChange = (index, value) => {
    const novasSecoes = [...secoes];
    novasSecoes[index].titulo = value;
    setSecoes(novasSecoes);
  };

  const handleAddPergunta = (secaoIndex) => {
    const novasSecoes = [...secoes];
    if (!novasSecoes[secaoIndex].perguntas) {
      novasSecoes[secaoIndex].perguntas = [];
    }
    novasSecoes[secaoIndex].perguntas.push({ texto_pergunta: '', tipo_pergunta: 'TEXTO', obrigatoria: true });
    setSecoes(novasSecoes);
  };

  const handleRemovePergunta = (secaoIndex, perguntaIndex) => {
    const novasSecoes = [...secoes];
    novasSecoes[secaoIndex].perguntas = novasSecoes[secaoIndex].perguntas.filter((_, i) => i !== perguntaIndex);
    setSecoes(novasSecoes);
  };

  const handlePerguntaChange = (secaoIndex, perguntaIndex, field, value) => {
    const novasSecoes = [...secoes];
    const perguntasArray = novasSecoes[secaoIndex].perguntas || novasSecoes[secaoIndex].checklist_perguntas_modelo;
    perguntasArray[perguntaIndex][field] = value;
    novasSecoes[secaoIndex].perguntas = perguntasArray;
    setSecoes(novasSecoes);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    const modeloData = {
      id: isEditing ? Number(id) : undefined,
      modelo_id: isEditing ? Number(id) : undefined,
      titulo,
      descricao,
      secoes: secoes.map(s => ({
        titulo: s.titulo,
        perguntas: (s.perguntas || s.checklist_perguntas_modelo || []).map(p => ({
          texto_pergunta: p.texto_pergunta,
          tipo_pergunta: p.tipo_pergunta,
          obrigatoria: p.obrigatoria,
          opcoes: p.tipo_pergunta === 'MULTIPLA_ESCOLHA' ? { options: [] } : null
        }))
      }))
    };

    try {
      await saveModelo(modeloData);
      toast.success('Modelo salvo com sucesso!');
      navigate('/checklist/modelos');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-6 text-center">Carregando modelo...</div>;
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-md text-sm mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Voltar
      </button>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-5 sm:p-6 border-b">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
            {isEditing ? 'Editar Modelo' : 'Novo Modelo de Checklist'}
          </h2>
        </div>

        {/* Campos básicos */}
        <div className="p-5 sm:p-6 space-y-5">
          <div className="min-w-0">
            <label htmlFor="titulo" className="block text-sm font-medium text-gray-700">Título do Modelo</label>
            {/* textarea: mais linhas e fonte menor no mobile */}
            <textarea
              id="titulo"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 text-sm md:text-xl md:font-semibold leading-6 md:leading-7 px-3 py-2.5 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-y md:resize-none min-h-[64px] md:min-h-[48px]"
              placeholder="Digite o título do modelo"
            />
          </div>

          <div className="min-w-0">
            <label htmlFor="descricao" className="block text-sm font-medium text-gray-700">Descrição</label>
            <textarea
              id="descricao"
              rows={4}
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 text-sm md:text-base leading-6 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-y"
              placeholder="Descrição do modelo (opcional)"
            />
          </div>
        </div>

        {/* Seções */}
        <div className="border-t p-5 sm:p-6 space-y-6">
          {secoes.map((secao, secaoIndex) => (
            <div key={secaoIndex} className="p-4 sm:p-5 border rounded-xl bg-gray-50">
              {/* Cabeçalho da Seção */}
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <label className="block text-sm text-gray-600 mb-1">Título da Seção</label>
                  {/* textarea para mobile com mais linhas e fonte menor */}
                  <textarea
                    value={secao.titulo}
                    onChange={(e) => handleSecaoChange(secaoIndex, e.target.value)}
                    className="w-full rounded-md border border-gray-300 text-sm md:text-base leading-6 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-y md:resize-none min-h-[56px] md:min-h-[44px]"
                    placeholder="Digite o título da seção"
                  />
                </div>

                {/* Botão excluir Seção - inline apenas no desktop */}
                {secoes.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveSecao(secaoIndex)}
                    className="hidden md:inline-flex self-start bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md"
                    aria-label="Remover seção"
                    title="Remover seção"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Linha de ações da Seção (MOBILE) */}
              {secoes.length > 1 && (
                <div className="mt-2 flex md:hidden justify-end">
                  <button
                    type="button"
                    onClick={() => handleRemoveSecao(secaoIndex)}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md"
                    aria-label="Remover seção"
                    title="Remover seção"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Perguntas */}
              <div className="mt-4 space-y-4">
                {(secao.perguntas || secao.checklist_perguntas_modelo || []).map((pergunta, perguntaIndex) => (
                  <div key={perguntaIndex} className="p-3 sm:p-4 bg-white rounded-lg border">
                    {/* Header da Pergunta (label + botão excluir no desktop) */}
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <label className="text-sm text-gray-600">Texto da pergunta</label>
                      <button
                        type="button"
                        onClick={() => handleRemovePergunta(secaoIndex, perguntaIndex)}
                        className="hidden md:inline-flex shrink-0 bg-red-600 hover:bg-red-700 text-white p-2 rounded-md"
                        aria-label="Remover pergunta"
                        title="Remover pergunta"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Campo da pergunta: textarea com mais linhas no mobile e fonte menor */}
                    <textarea
                      value={pergunta.texto_pergunta}
                      onChange={(e) => handlePerguntaChange(secaoIndex, perguntaIndex, 'texto_pergunta', e.target.value)}
                      className="block w-full rounded-md border border-gray-300 text-sm md:text-base leading-6 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-y md:resize-none min-h-[64px] md:min-h-[44px]"
                      placeholder="Digite o texto da pergunta"
                      required
                    />

                    {/* Tipo de resposta */}
                    <div className="mt-3">
                      <label className="block text-sm text-gray-600 mb-1">Tipo de resposta</label>
                      <select
                        value={pergunta.tipo_pergunta}
                        onChange={(e) => handlePerguntaChange(secaoIndex, perguntaIndex, 'tipo_pergunta', e.target.value)}
                        className="block w-full rounded-md border border-gray-300 text-sm md:text-base py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="MULTIPLA_ESCOLHA">Status de Conformidade</option>
                        <option value="TEXTO">Texto</option>
                        <option value="SIM_NAO">Sim / Não</option>
                      </select>
                    </div>

                    {/* Linha de ações da Pergunta (MOBILE) */}
                    <div className="mt-3 flex md:hidden justify-end">
                      <button
                        type="button"
                        onClick={() => handleRemovePergunta(secaoIndex, perguntaIndex)}
                        className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-md"
                        aria-label="Remover pergunta"
                        title="Remover pergunta"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => handleAddPergunta(secaoIndex)}
                  className="w-full sm:w-auto justify-center text-sm bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-md inline-flex items-center gap-2"
                >
                  <PlusCircle className="h-4 w-4" />
                  Adicionar Pergunta
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={handleAddSecao}
            className="w-full sm:w-auto justify-center bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2"
          >
            <PlusCircle className="h-5 w-5" />
            Adicionar Seção
          </button>
        </div>

        <div className="p-5 sm:p-6 bg-gray-50 border-t">
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className="w-full sm:w-auto bg-purple-600 text-white font-bold py-2.5 px-6 rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
            >
              {isSaving ? 'Salvando...' : 'Salvar Modelo'}
            </button>
          </div>
        </div>

        {error && <div className="p-4 bg-red-100 text-red-700 text-center">{error}</div>}
      </form>
    </div>
  );
};

export default ModeloForm;